import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// MUI
import {
  Container,
  Box,
  Typography,
  MenuItem,
  InputLabel,
  Divider,
  TextField,
  Checkbox,
  Tooltip,
} from '@mui/material';
import Select from '~/components/General/OlcSelect';

import PricingControlsTable from '~/components/AdminPortal/PricingControls/SharedTable';
import OlcActionMenu from '~/components/General/OlcActionMenu';
import OlcButton from '~/components/General/OlcButton';
import OlcDropdownSearch from '~/components/General/OlcDropdownSearch';
import OlcDropdownChevron from '~/components/General/OlcDropdownChevron';
import TruncatedText from '~/components/General/TruncatedText';
import { ContactDetailsDeleteCIcon, CheckedCIcon, UncheckedCIcon } from '~/assets/images';
import OLCBaseModal from '../BaseModal';
import {
  updateAdminPlanFeatures,
  assignPlanToAccount,
  deletePlanVersion,
  fetchAdminPlansFiltersData,
  fetchAdminPlansVersionsData,
  fetchAdminPlansById,
  fetchAdminPlansOptions,
} from '~/redux/actions/admin-pricing-actions';
import { SET_ADMIN_PRICING_PLANS_DATA } from '~/redux/actions/action-types';
import { success, failure } from '~/redux/actions/snackbar-actions';
import { dateFormat } from '~/utils/date-format';

import '../styles.scss';
import './styles.scss';
import ConfirmModal from '~/components/General/ConfirmModal';
import AssignPlanModal from './AssignPlanModal';
import UpdatePlanModal from './UpdatePlanModal';

// Format date to YYYY-MM-DD format for API payloads
const formatDateForAPI = (date) => {
  if (!date) return null;
  if (typeof date === 'string') {
    // If it's already a string, extract just the date part
    return date.split('T')[0];
  }
  if (date instanceof Date) {
    // Format Date object to YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return null;
};

// Transform API response data into table rows
const transformPlansDataToRows = (plansData, planOptions = []) => {
  if (!plansData || !Array.isArray(plansData) || plansData.length === 0) {
    console.log('No plans data or empty array');
    return [];
  }

  const rows = [];

  // Get all unique features across all plans to build rows
  const featureMap = new Map();

  // First, collect all unique features from all plans
  const allFeaturesMap = new Map();
  plansData.forEach((plan) => {
    const features = plan.features || [];
    features.forEach((feature) => {
      if (!allFeaturesMap.has(feature.slug)) {
        allFeaturesMap.set(feature.slug, feature);
      }
    });
  });

  // Check if any plan has is_standard false (to determine if dropdown should be shown)
  const hasNonStandardPlan = plansData.some((plan) => plan.is_standard === false);

  plansData.forEach((plan, planIndex) => {
    const planField = `plan${planIndex + 1}`;
    const features = plan.features || [];
    // Read is_standard from the current plan object
    const planIsStandard = plan.is_standard !== false; // Default to true if not set

    // Row 1: Stripe Plan
    if (!featureMap.has('stripe_plan')) {
      // If any plan has is_standard false, make it a dropdown; otherwise keep as read-only input
      featureMap.set('stripe_plan', {
        id: 'stripe_plan',
        feature: 'Stripe Plan',
        slug: 'stripe_plan',
        readOnly: !hasNonStandardPlan, // Read-only when all plans have is_standard true
        controlType: !hasNonStandardPlan ? 'input' : 'select', // Dropdown when any plan has is_standard false
        options: !hasNonStandardPlan ? [] : planOptions, // Add plan options when any plan has is_standard false
      });
    }
    const stripePlanValue = plan.labels?.stripe_plan || `${plan.plan_id} - ${plan.plan_name}`;
    featureMap.get('stripe_plan')[planField] = stripePlanValue;
    featureMap.get('stripe_plan')[`${planField}_hover`] = plan.labels?.stripe_plan_hover || '';
    featureMap.get('stripe_plan')[`${planField}_plan_id`] = plan.plan_id;
    // Store the plan ID as value for dropdown
    featureMap.get('stripe_plan')[`${planField}_value`] = plan.plan_id;

    // Row 2: Price (dynamic name based on stripe_plan_duration)
    const priceFieldName = plan.labels?.stripe_plan_duration
      ? `${plan.labels.stripe_plan_duration} Price`
      : 'Price';
    if (!featureMap.has('price')) {
      featureMap.set('price', {
        id: 'price',
        feature: priceFieldName,
        slug: 'price',
        readOnly: true,
        controlType: 'input',
      });
    }
    featureMap.get('price')[planField] = plan.labels?.stripe_plan_price || plan.plan_amount || '';
    featureMap.get('price').feature = priceFieldName; // Update with latest duration

    // Row 3: Plan Duration
    if (!featureMap.has('plan_duration')) {
      featureMap.set('plan_duration', {
        id: 'plan_duration',
        feature: 'Plan Duration',
        slug: 'plan_duration',
        readOnly: true,
        controlType: 'select',
      });
    }
    featureMap.get('plan_duration')[planField] =
      plan.labels?.stripe_plan_duration || plan.plan_duration || '';

    // Process features - use the feature definition from allFeaturesMap
    allFeaturesMap.forEach((featureDef, slug) => {
      if (!featureMap.has(slug)) {
        featureMap.set(slug, {
          id: slug,
          feature: featureDef.title,
          slug: slug,
          controlType:
            featureDef.field_type === 'tier'
              ? 'tier'
              : featureDef.field_type === 'dropdown'
                ? 'select'
                : 'input',
          options: featureDef.options || [],
          fieldType: featureDef.field_type,
          tiers: featureDef.tiers || [],
        });
      }

      // Find the feature value for this specific plan
      const planFeature = features.find((f) => f.slug === slug);
      if (planFeature) {
        // Handle false explicitly - show "No" instead of "-"
        if (planFeature.formatted_value === false) {
          featureMap.get(slug)[planField] = 'No';
        } else {
          let displayValue = planFeature.formatted_value || planFeature.value || '';

          // Special handling for tier features - format as "Tiers 1, 2, 3"
          if (featureDef.field_type === 'tier' && displayValue) {
            const tierNumbers = displayValue
              .split(',')
              .map((t) => {
                const match = t.trim().match(/(\d+)/);
                return match ? parseInt(match[1]) : null;
              })
              .filter((n) => n !== null)
              .sort((a, b) => a - b);

            if (tierNumbers.length === 1) {
              displayValue = `Tier ${tierNumbers[0]}`;
            } else if (tierNumbers.length > 1) {
              displayValue = `Tiers ${tierNumbers.join(', ')}`;
            }
          }

          featureMap.get(slug)[planField] = displayValue;
        }
        // Store actual value for editing
        featureMap.get(slug)[`${planField}_value`] = planFeature.value;
      } else {
        // If this plan doesn't have this feature, set empty
        featureMap.get(slug)[planField] = '';
        featureMap.get(slug)[`${planField}_value`] = null;
      }
      // Store the plan's is_standard value for each row
      featureMap.get(slug)[`${planField}_is_standard`] = planIsStandard;
    });
  });

  // Convert map to array
  const featureRows = Array.from(featureMap.values());

  // Sort rows: Stripe Plan, Price, Duration, then features
  const orderedRows = [
    featureRows.find((r) => r.slug === 'stripe_plan'),
    featureRows.find((r) => r.slug === 'price'),
    featureRows.find((r) => r.slug === 'plan_duration'),
    ...featureRows.filter((r) => !['stripe_plan', 'price', 'plan_duration'].includes(r.slug)),
  ].filter(Boolean);

  return orderedRows;
};

// Transform API response for version details table
const transformVersionDetailsData = (plansData) => {
  if (!plansData || Object.keys(plansData).length === 0) {
    return [];
  }

  return {
    id: plansData.change_type + plansData.status,
    effectiveDate: plansData.effective_date ? dateFormat(plansData.effective_date, true, true) : '-',
    status: plansData.status || '-',
    changeType: plansData.change_type || '-',
    notes: plansData.notes || '-',
  };
};

const buildColumns = (
  plansData,
  {
    isEditing,
    setRows,
    onOpenTierModal,
    isStandard = true,
    planOptions = [],
    account = null,
    planIsStandardMap = {},
    setPlanIsStandardMap,
  }
) => {
  const base = [
    {
      field: 'feature',
      headerName: 'Feature',
      flex: 1.2,
      minWidth: 260,
      sortable: false,
      renderCell: (params) => (
        <Box className="fullText">
          <TruncatedText text={params.row.feature || ''} />
        </Box>
      ),
    },
  ];

  if (!plansData || !Array.isArray(plansData) || plansData.length === 0) {
    return base;
  }

  const planColumns = plansData.map((plan, idx) => {
    const field = `plan${idx + 1}`;
    const headerName = plan.plan_name || `Plan ${idx + 1}`;

    return {
      field,
      headerName,
      flex: 1,
      minWidth: 220,
      sortable: false,
      renderCell: (params) => {
        const { controlType, options, readOnly, fieldType, tiers } = params.row || {};
        const value = params.row[field] ?? '';
        const rowId = params.row.id;
        const hoverText = params.row[`${field}_hover`] || '';
        // Check if this plan column should be disabled (is_standard is true)
        // First check planIsStandardMap (from dropdown selection), then check row data, then check plan object
        const planIsStandardFromMap = planIsStandardMap[field];
        const planIsStandardFromRow = params.row[`${field}_is_standard`];
        const currentPlan = plansData.find((_, idx) => `plan${idx + 1}` === field);
        const planIsStandard =
          planIsStandardFromMap !== undefined
            ? planIsStandardFromMap
            : planIsStandardFromRow !== undefined
              ? planIsStandardFromRow
              : currentPlan?.is_standard !== false;
        // Don't disable columns if account id is null
        const isPlanDisabled = account?.id != null && planIsStandard === true;

        // Always render plain text when not editing, or for the feature column
        if (!isEditing || field === 'feature') {
          // Special handling for Stripe Plan with hover tooltip
          if (rowId === 'stripe_plan' && hoverText) {
            return (
              <Tooltip title={hoverText} placement="top" arrow>
                <Box className="addressWrapper">
                  <Typography>{value}</Typography>
                </Box>
              </Tooltip>
            );
          }
          // Show "No" if value is false or if value is "No"
          const displayValue = value === false || value === 'No' ? 'No' : value || '';
          return (
            <Box className="addressWrapper">
              <TruncatedText text={displayValue} />
            </Box>
          );
        }

        // Read-only rows (first three summary fields) should look like inputs
        // but not be editable.
        // Special case: stripe_plan can be a dropdown when is_standard is false
        if (readOnly && rowId !== 'stripe_plan') {
          const isFirstSummaryRow = rowId === 1;
          return (
            <Box className="addressWrapper plansInputCell">
              <TextField
                size="small"
                value={value}
                className={`cellInput readOnlyCellInput ${
                  isFirstSummaryRow ? 'readOnlyCellInputWide' : ''
                }`}
                InputProps={{ readOnly: true }}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              />
            </Box>
          );
        }

        // Handle stripe_plan as dropdown when is_standard is false (readOnly will be false)
        if (rowId === 'stripe_plan' && controlType === 'select' && options && options.length > 0) {
          const planValue = params.row[`${field}_value`] || params.row[`${field}_plan_id`] || '';
          const optionValues = options.map((opt) => (typeof opt === 'object' ? opt.id : opt));
          const optionLabels = options.map((opt) => (typeof opt === 'object' ? opt.title : opt));

          // Find current plan in options
          const currentPlanIndex = optionValues.findIndex(
            (optId) => String(optId) === String(planValue)
          );
          const currentValue =
            currentPlanIndex >= 0 ? optionValues[currentPlanIndex] : planValue || '';

          return (
            <Box className="addressWrapper plansInputCell">
              <Select
                size="small"
                value={currentValue}
                className="cellInput"
                IconComponent={(props) => (
                  <OlcDropdownChevron
                    {...props}
                    style={{
                      ...props.style,
                      top: '42%',
                      right: '10px',
                    }}
                  />
                )}
                onChange={(e) => {
                  const selectedPlanId = e.target.value;
                  // Find the plan option
                  const selectedPlanIndex = optionValues.findIndex(
                    (optId) => String(optId) === String(selectedPlanId)
                  );
                  const selectedPlan = selectedPlanIndex >= 0 ? options[selectedPlanIndex] : null;
                  const selectedLabel = selectedPlan
                    ? typeof selectedPlan === 'object'
                      ? selectedPlan.title
                      : selectedPlan
                    : '';

                  // Check if selected plan has is_standard true
                  const isStandard = selectedPlan?.is_standard === true;

                  // Update plan is_standard map
                  setPlanIsStandardMap((prev) => ({
                    ...prev,
                    [field]: isStandard,
                  }));

                  setRows((prev) =>
                    prev.map((row) => {
                      // Update Stripe Plan row
                      if (row.id === rowId) {
                        const updated = { ...row };
                        updated[field] = selectedLabel;
                        updated[`${field}_value`] = selectedPlanId;
                        updated[`${field}_plan_id`] = selectedPlanId;
                        updated[`${field}_hover`] = selectedPlan?.stripe_id || '';
                        return updated;
                      }
                      // Update Price row with selected plan's amount
                      if (row.slug === 'price' && selectedPlan) {
                        const updated = { ...row };
                        // Format price similar to how it's stored in the original data
                        const planAmount =
                          selectedPlan.amount != null
                            ? typeof selectedPlan.amount === 'string'
                              ? selectedPlan.amount
                              : `$${parseFloat(selectedPlan.amount).toFixed(2)}`
                            : '';
                        updated[field] = planAmount;
                        updated[`${field}_value`] = selectedPlan.amount;
                        return updated;
                      }
                      // Update Plan Duration row with selected plan's duration
                      if (row.slug === 'plan_duration' && selectedPlan) {
                        const updated = { ...row };
                        // Use duration field (mapped from duration_type in planOptions)
                        const planDuration = selectedPlan.duration || '';
                        updated[field] = planDuration;
                        updated[`${field}_value`] = planDuration;
                        return updated;
                      }
                      return row;
                    })
                  );
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {optionValues.map((optValue, idx) => (
                  <MenuItem key={optValue} value={optValue}>
                    {optionLabels[idx] || optValue}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          );
        }

        // Select controls (dropdown field type)
        if (controlType === 'select' || fieldType === 'dropdown') {
          // For dropdown fields, use the option value, not label
          const optionValues = (options || []).map((opt) =>
            typeof opt === 'object' ? opt.value : opt
          );
          const optionLabels = (options || []).map((opt) =>
            typeof opt === 'object' ? opt.label : opt
          );

          // Find current value index - check for "No" (when formatted_value is false) or value matches label
          let currentValueIndex = optionLabels.findIndex((label) => label === value);

          // If value is "No" (formatted_value was false), find the option with value false
          if (value === 'No' && currentValueIndex === -1) {
            currentValueIndex = optionValues.findIndex((val) => val === false);
          }

          // If still not found, check if stored value is false
          const storedValue = params.row[`${field}_value`];
          if (currentValueIndex === -1 && storedValue === false) {
            currentValueIndex = optionValues.findIndex((val) => val === false);
          }

          const currentValue =
            currentValueIndex >= 0
              ? optionValues[currentValueIndex]
              : storedValue !== undefined
                ? storedValue
                : value;

          return (
            <Box className="addressWrapper plansInputCell">
              <Select
                size="small"
                value={currentValue}
                className="cellInput"
                disabled={isPlanDisabled}
                IconComponent={(props) => (
                  <OlcDropdownChevron
                    {...props}
                    style={{
                      ...props.style,
                      top: '42%',
                      right: '10px',
                    }}
                  />
                )}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  // Find the label for the selected value
                  const selectedIndex = optionValues.findIndex((val) => val === selectedValue);
                  const selectedLabel =
                    selectedIndex >= 0 ? optionLabels[selectedIndex] : selectedValue;

                  const rowId = params.row.id;
                  setRows((prev) =>
                    prev.map((row) => {
                      if (row.id === rowId) {
                        const updated = { ...row, [field]: selectedLabel };
                        // Also update the value field for API
                        updated[`${field}_value`] = selectedValue;
                        return updated;
                      }
                      return row;
                    })
                  );
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {optionValues.map((optValue, idx) => (
                  <MenuItem key={optValue} value={optValue}>
                    {optionLabels[idx] || optValue}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          );
        }

        // Tier pricing access – open modal
        if (controlType === 'tier') {
          // Parse tier values and extract numbers for clean display
          let tierNumbers = [];
          if (value) {
            // Extract numbers from tier strings (handles both "Tier 1" and "1" formats)
            tierNumbers = value
              .split(',')
              .map((t) => {
                const match = t.trim().match(/(\d+)/);
                return match ? parseInt(match[1]) : null;
              })
              .filter((n) => n !== null)
              .sort((a, b) => a - b);
          }

          // Format as "Tiers 1, 2, 3" format and split into 2 lines
          let displayText = '';
          if (tierNumbers.length === 1) {
            displayText = `Tier ${tierNumbers[0]}`;
          } else if (tierNumbers.length > 1) {
            displayText = `Tiers ${tierNumbers.join(', ')}`;
          }

          // Split the display text into 2 lines if too long
          const tierValues = displayText ? [displayText] : [];
          let firstLineTiers = [];
          let secondLineTiers = [];

          if (tierNumbers.length > 4) {
            // For many tiers, split the numbers across two lines
            const midPoint = Math.ceil(tierNumbers.length / 2);
            const firstHalf = tierNumbers.slice(0, midPoint);
            const secondHalf = tierNumbers.slice(midPoint);
            firstLineTiers = [`Tiers ${firstHalf.join(', ')}`];
            secondLineTiers = [`${secondHalf.join(', ')}`];
          } else {
            // For few tiers, show on one line
            firstLineTiers = tierValues;
          }

          return (
            <Box className="plansTierCell">
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1, minWidth: 0 }}
              >
                {firstLineTiers.length > 0 && (
                  <Typography
                    sx={{
                      lineHeight: 1.3,
                      wordBreak: 'break-word',
                      whiteSpace: 'normal',
                      fontSize: '14px',
                      width: '100%',
                    }}
                  >
                    {firstLineTiers.join(', ')}
                  </Typography>
                )}
                {secondLineTiers.length > 0 && (
                  <Typography
                    sx={{
                      lineHeight: 1.3,
                      wordBreak: 'break-word',
                      whiteSpace: 'normal',
                      fontSize: '14px',
                      width: '100%',
                    }}
                  >
                    {secondLineTiers.join(', ')}
                  </Typography>
                )}
              </Box>
              <OlcButton
                variant="secondary"
                size="small"
                className="tierUpdateBtn"
                disabled={isPlanDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenTierModal(field);
                }}
              >
                Update
              </OlcButton>
            </Box>
          );
        }

        // Default to simple text input (for currency, number, etc.)
        // Disable if plan has is_standard true
        return (
          <Box className="addressWrapper plansInputCell">
            <TextField
              size="small"
              value={value}
              type={fieldType === 'number' ? 'number' : 'text'}
              disabled={isPlanDisabled || readOnly}
              className={`cellInput ${readOnly ? 'readOnlyCellInput' : ''}`}
              onChange={(e) => {
                const next = e.target.value;
                const rowId = params.row.id;
                setRows((prev) =>
                  prev.map((row) => {
                    if (row.id === rowId) {
                      const updated = { ...row, [field]: next };
                      // Also update the value field for API
                      updated[`${field}_value`] =
                        fieldType === 'number' ? parseFloat(next) || 0 : next;
                      return updated;
                    }
                    return row;
                  })
                );
              }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </Box>
        );
      },
    };
  });

  return [...base, ...planColumns];
};

const buildVersionColumns = () => [
  { field: 'effectiveDate', headerName: 'Effective Date', flex: 1, width: 200, sortable: false },
  { field: 'status', headerName: 'Status', flex: 1, width: 200, sortable: false },
  { field: 'changeType', headerName: 'Change Type', flex: 1, width: 200, sortable: false },
  { field: 'notes', headerName: 'Notes', flex: 2, width: 200, sortable: false },
];

const Plans = ({ planCount = 2 }) => {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [versionRows, setVersionRows] = useState([]);
  const [isEditing, setIsEditing] = useState(false);

  // Redux state
  const plansData = useSelector((state) => state.adminPricingReducers.plans);
  const { data, features, versions, loading } = plansData;

  // Get plans data from Redux (should be array from API)
  // The reducer stores data in `data` field, which should be an array
  const plansArray = Array.isArray(data) ? data : data ? [data] : [];
  const [tierModalOpen, setTierModalOpen] = useState(false);
  const [tierModalPlanField, setTierModalPlanField] = useState(null); // 'plan1' | 'plan2' | 'plan3'
  const [tierAccessByPlan, setTierAccessByPlan] = useState({
    plan1: { 1: true, 2: true, 3: true, 4: true, 5: true, 6: true },
    plan2: { 3: true, 4: true, 5: true, 6: true },
    plan3: {},
  });

  // State declarations for filters (moved before columns useMemo to avoid initialization error)
  const [account, setAccount] = useState(null);
  const [planOptions, setPlanOptions] = useState([]);
  // Track which plans have is_standard true (key: planField like 'plan1', value: boolean)
  const [planIsStandardMap, setPlanIsStandardMap] = useState({});

  const columns = useMemo(
    () =>
      buildColumns(plansArray, {
        isEditing,
        setRows,
        isStandard: true, // This is now determined per plan, not from account
        planOptions,
        account,
        planIsStandardMap,
        setPlanIsStandardMap,
        onOpenTierModal: (planField) => {
          // Initialize tier access from current row data
          const tiersRow = rows.find((row) => row.slug === 'tiers');
          if (tiersRow) {
            const tierValue = tiersRow[`${planField}_value`] || tiersRow[planField];
            if (Array.isArray(tierValue)) {
              const tierAccess = {};
              tierValue.forEach((tierSlug) => {
                const tierId = tierSlug ? parseInt(tierSlug.replace('tier_', '')) : null;
                if (tierId) {
                  tierAccess[tierId] = true;
                }
              });
              setTierAccessByPlan((prev) => ({
                ...prev,
                [planField]: tierAccess,
              }));
            }
          }
          setTierModalPlanField(planField);
          setTierModalOpen(true);
        },
      }),
    [plansArray, isEditing, rows, account, planOptions, planIsStandardMap]
  );
  const versionColumns = useMemo(() => buildVersionColumns(), []);

  // State declarations for filters
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [featureVersion, setFeatureVersion] = useState(null);
  const [accountOptions, setAccountOptions] = useState([]);
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const [featureVersionOptions, setFeatureVersionOptions] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState({
    open: false,
    header: 'Delete Plan Version',
    message:
      'Are you sure you want to delete this Plan?\n\nIf you continue, the previous Plan Version will remain in effect until a new version becomes active.',
    successButtonName: 'Yes',
    cancelButtonName: 'No',
    loading: false,
    icon: ContactDetailsDeleteCIcon,
  });
  const actionsOpen = Boolean(actionsAnchorEl);
  const [assignPlanOpen, setAssignPlanOpen] = useState(false);
  const [updatePlanOpen, setUpdatePlanOpen] = useState(false);
  const [updatePlanFormData, setUpdatePlanFormData] = useState({
    effectiveDate: null,
    changeType: null,
    notes: '',
  });

  const handleStartEdit = () => {
    setIsEditing(true);
    handleActionsClose();
  };

  const handleSave = async () => {
    try {
      // Build payload according to API structure
      // Each plan needs subscription_version_id and features array
      const plansPayload = plansArray.map((plan, planIndex) => {
        const planField = `plan${planIndex + 1}`;

        // Collect features for this plan (excluding read-only fields like stripe_plan, price, plan_duration)
        const planFeatures = rows
          .filter(
            (row) =>
              !row.readOnly &&
              row.slug &&
              row.slug !== 'stripe_plan' &&
              row.slug !== 'price' &&
              row.slug !== 'plan_duration'
          )
          .map((row) => {
            const valueField = `${planField}_value`;
            let value =
              row[valueField] !== undefined && row[valueField] !== null
                ? row[valueField]
                : row[planField];

            // Handle tier values - should be array of tier slugs
            if (row.slug === 'tiers') {
              if (Array.isArray(value)) {
                // Value is already an array of tier slugs
              } else if (typeof value === 'string') {
                // If it's a string like "Tiers 1, 2", we need to get the actual tier slugs
                // This should have been stored in _value field, but if not, try to parse
                const tierAccess = tierAccessByPlan[planField] || {};
                const enabledTierIds = Object.entries(tierAccess)
                  .filter(([_, enabled]) => enabled)
                  .map(([tierId, _]) => `tier_${tierId}`);
                value = enabledTierIds.length > 0 ? enabledTierIds : [];
              } else {
                // Fallback to empty array
                value = [];
              }
            } else {
              // Handle dropdown values - convert display values to actual values
              if (row.fieldType === 'dropdown' && row.options && row.options.length > 0) {
                const option = row.options.find(
                  (opt) => opt.value === value || opt.label === value
                );
                if (option) {
                  value = option.value;
                }
                // Convert "yes"/"no" strings to boolean if needed
                if (value === 'yes') value = true;
                if (value === 'no') value = false;
              }

              // Handle number fields - ensure they're numbers
              if (row.fieldType === 'number' || row.fieldType === 'currency') {
                if (typeof value === 'string') {
                  // Remove currency symbols and parse
                  const numValue = parseFloat(value.replace(/[^0-9.-]/g, ''));
                  value = isNaN(numValue) ? 0 : numValue;
                } else if (typeof value !== 'number') {
                  value = 0;
                }
              }
            }

            return {
              slug: row.slug,
              value: value,
            };
          })
          .filter(
            (feature) =>
              feature.value !== undefined && feature.value !== null && feature.value !== ''
          );

        const subscriptionVersionId = plan?.id || featureVersion?.id;

        if (!subscriptionVersionId) {
          throw new Error(`Missing subscription_version_id for plan ${planIndex + 1}`);
        }

        // Get the updated plan_id from rows if it was changed via dropdown
        const stripePlanRow = rows.find((row) => row.slug === 'stripe_plan');
        const updatedPlanId =
          stripePlanRow?.[`${planField}_plan_id`] ||
          stripePlanRow?.[`${planField}_value`] ||
          plan.plan_id;

        return {
          subscription_version_id: subscriptionVersionId,
          plan_id: updatedPlanId,
          features: planFeatures,
        };
      });

      // Get effective_date, notes, and change_type from form data or version details
      // If updatePlanFormData is set, use it; otherwise use data from first plan
      const effectiveDate = updatePlanFormData.effectiveDate
        ? formatDateForAPI(updatePlanFormData.effectiveDate)
        : plansArray[0]?.effective_date
          ? formatDateForAPI(plansArray[0].effective_date)
          : null;

      const notes = updatePlanFormData.notes || plansArray[0]?.notes || '';

      // Map change type - handle both object and string formats
      let changeType = updatePlanFormData.changeType;
      if (changeType && typeof changeType === 'object') {
        changeType = changeType.id || changeType.value || changeType;
      }
      if (!changeType) {
        changeType = plansArray[0]?.change_type || 'general_update';
      }
      // Ensure changeType is a string before processing
      changeType = String(changeType || 'general_update');
      // Convert to lowercase, replace spaces with underscores, and convert kebab-case to snake_case
      changeType = changeType.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');

      const payload = {
        plans: plansPayload,
        effective_date: effectiveDate,
        notes: notes,
        change_type: changeType,
      };

      const response = await dispatch(updateAdminPlanFeatures(payload));
      if (response.status === 200) {
        dispatch(success(response.data.message || 'Plan features updated successfully'));
        setIsEditing(false);
        // Close the UpdatePlanModal
        setUpdatePlanOpen(false);
        // Clear update plan form data
        setUpdatePlanFormData({
          effectiveDate: null,
          changeType: null,
          notes: '',
        });
        // Re-fetch versions to get the latest version with is_default=true
        await fetchVersionsData(account.id || '', true);
      } else {
        dispatch(failure(response?.data?.message || 'Failed to update plan features'));
        // Re-fetch data even on error to get latest state
        if (featureVersion?.id) {
          fetchPlansDataByVersion(featureVersion.id);
        }
      }
    } catch (error) {
      dispatch(
        failure(
          error?.response?.data?.message || error?.message || 'Failed to update plan features'
        )
      );
      // Re-fetch data even on error to get latest state
      if (featureVersion?.id) {
        fetchPlansDataByVersion(featureVersion.id);
      }
    }
  };

  const handleCancel = () => {
    // Reset rows from API data
    if (plansArray.length > 0) {
      const transformedRows = transformPlansDataToRows(plansArray, planOptions);
      setRows(transformedRows);
    }
    setIsEditing(false);
    // Re-fetch plan data to get latest values from server
    if (featureVersion?.id) {
      fetchPlansDataByVersion(featureVersion.id);
    }
  };

  // Fetch plan options when account changes and is_standard is false
  const fetchPlanOptions = async () => {
    try {
      if (account && account.is_standard === false) {
        const response = await dispatch(fetchAdminPlansOptions());
        if (response.status === 200) {
          const { data } = response.data;
          if (data.plans && Array.isArray(data.plans)) {
            const mappedPlans = data.plans.map((plan) => ({
              id: plan.id,
              title: plan.label,
              stripe_id: plan.stripe_id,
              amount: plan.amount,
              duration: plan.duration_type,
              is_standard: plan.is_standard,
            }));
            setPlanOptions(mappedPlans);
          }
        }
      } else {
        setPlanOptions([]);
      }
    } catch (error) {
      console.error('Failed to fetch plan options:', error);
      setPlanOptions([]);
    }
  };

  const handleActionsClick = (event) => {
    setActionsAnchorEl(event.currentTarget);
  };

  const handleActionsClose = () => {
    setActionsAnchorEl(null);
  };

  // Fetch plans data by version ID
  const fetchPlansDataByVersion = async (versionId) => {
    try {
      const response = await dispatch(fetchAdminPlansById(versionId));
      if (response.status === 200) {
        // The data will be automatically updated in Redux store
        // and the component will re-render via the existing useEffect
      } else {
        dispatch(failure(response?.data?.message || 'Failed to load plans data'));
      }
    } catch (error) {
      console.error('Failed to fetch plans data:', error);
      dispatch(failure('Failed to load plans data'));
    }
  };

  // Fetch versions data based on selected account
  const fetchVersionsData = async (orgId, autoSelectDefault = false) => {
    try {
      // Always call the API, even with empty org_id if account label is present
      const response = await dispatch(fetchAdminPlansVersionsData({ org_id: orgId || '' }));
      if (response.status === 200) {
        const { data } = response.data;

        // Map version options - handle object response with numeric keys
        let mappedVersions = [];
        if (data) {
          // Check if data is an object (not an array) - response format: { "0": {...}, "1": {...} }
          if (typeof data === 'object' && !Array.isArray(data)) {
            // Convert object to array by getting all values
            const versionsArray = Object.values(data);
            mappedVersions = versionsArray.map((version) => ({
              id: version.id || version.value,
              title: version.version || version.name || version.label || version.title,
              is_default: version.is_default || false,
              status: version.status || false,
            }));
          } else if (Array.isArray(data)) {
            // Handle array response (backward compatibility)
            mappedVersions = data.map((version) => ({
              id: version.id || version.value,
              title: version.version || version.name || version.label || version.title,
              is_default: version.is_default || false,
              status: version.status || false,
            }));
          }

          setFeatureVersionOptions(mappedVersions);

          // Auto-select default version only if autoSelectDefault is true (on component mount)
          if (autoSelectDefault) {
            const defaultVersion = mappedVersions.find((v) => v.is_default === true);
            if (defaultVersion) {
              setFeatureVersion(defaultVersion);
              // Fetch plan data for the default version
              setTimeout(() => {
                fetchPlansDataByVersion(defaultVersion.id);
              }, 0);
            } else if (mappedVersions.length > 0) {
              // If no default, select the first one
              setFeatureVersion(mappedVersions[0]);
              // Fetch plan data for the first version
              setTimeout(() => {
                fetchPlansDataByVersion(mappedVersions[0].id);
              }, 0);
            } else {
              setFeatureVersion(null);
            }
          }
        } else {
          setFeatureVersionOptions([]);
          if (autoSelectDefault) {
            setFeatureVersion(null);
          }
        }

        // Return the mapped versions count for checking
        return mappedVersions.length;
      }
      return 0;
    } catch (error) {
      console.error('Failed to fetch versions data:', error);
      dispatch(failure('Failed to load versions data'));
      setFeatureVersionOptions([]);
      if (autoSelectDefault) {
        setFeatureVersion(null);
      }
      return 0;
    }
  };

  // Fetch filters data on component mount
  const fetchFiltersData = async () => {
    try {
      setFiltersLoading(true);
      const response = await dispatch(fetchAdminPlansFiltersData());
      if (response.status === 200) {
        const { data } = response.data;

        // Map account options
        let mappedAccounts = [];
        if (data.accounts && Array.isArray(data.accounts)) {
          mappedAccounts = data.accounts.map((account) => ({
            id: account.id || account.value,
            title: account.name || account.label || account.title,
            is_default: account.is_default || false,
            is_standard: account.is_standard || false,
            status: account.status || false,
          }));
          setAccountOptions(mappedAccounts);
        }

        // Auto-select account with id === null on component mount
        const accountWithNullId = mappedAccounts.find(
          (acc) => acc.id === null || acc.id === undefined || acc.id === ''
        );
        if (accountWithNullId) {
          setAccount(accountWithNullId);
          // Fetch versions for the account with null id (use empty string for API call)
          // Auto-select default version on initial mount only
          fetchVersionsData('', true);
        } else {
          // Fallback: Auto-select default account if no null id account found
          const defaultAccount = mappedAccounts.find((acc) => acc.is_default === true);
          if (defaultAccount) {
            setAccount(defaultAccount);
            // Fetch versions for the default account (use id if available, otherwise empty string)
            // Auto-select default version on initial mount only
            fetchVersionsData(defaultAccount.id || '', true);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch filters data:', error);
      dispatch(failure('Failed to load filters data'));
    } finally {
      setFiltersLoading(false);
    }
  };

  const handleTierToggle = (tierId) => {
    if (!tierModalPlanField) return;

    setTierAccessByPlan((prev) => {
      const current = { ...(prev[tierModalPlanField] || {}) };
      const isCurrentlyChecked = !!current[tierId];

      // Simply toggle the tier - no restrictions on selection order
      current[tierId] = !isCurrentlyChecked;
      return {
        ...prev,
        [tierModalPlanField]: current,
      };
    });
  };

  const handleTierModalClose = () => {
    setTierModalOpen(false);
    setTierModalPlanField(null);
  };

  const handleTierModalSave = () => {
    // Get tiers from the row data
    const tiersRow = rows.find((row) => row.slug === 'tiers');
    const availableTiers = tiersRow?.tiers || [];

    // Get enabled tiers
    const enabledTierIds = Object.entries(tierAccessByPlan[tierModalPlanField] || {})
      .filter(([_, enabled]) => enabled)
      .map(([tierId, _]) => parseInt(tierId))
      .sort((a, b) => a - b);

    // Validate sequential order - check for gaps between first and last selected tier
    if (enabledTierIds.length > 0) {
      const minTierId = enabledTierIds[0];
      const maxTierId = enabledTierIds[enabledTierIds.length - 1];

      // Find missing tiers between first and last selected tier
      const missingTierIds = [];
      for (let i = minTierId; i <= maxTierId; i++) {
        if (!enabledTierIds.includes(i)) {
          missingTierIds.push(i);
        }
      }

      // If there are gaps, show validation message with specific missing tiers
      if (missingTierIds.length > 0) {
        const missingTiersText = missingTierIds.map((id) => `Tier ${id}`).join(', ');
        dispatch(
          failure(
            `Please select all tiers from Tier ${minTierId} to Tier ${maxTierId}. Missing tiers: ${missingTiersText}.`
          )
        );
        return; // Don't save if validation fails
      }
    }

    // Build tier access payload using tier slugs
    const enabledTierSlugs = enabledTierIds.map((tierId) => {
      const tier = availableTiers.find((t) => {
        const tierNum = t.slug ? parseInt(t.slug.replace('tier_', '')) : t.id;
        return tierNum === tierId;
      });
      return tier?.slug || `tier_${tierId}`;
    });

    // No API call - just save locally and trigger UpdatePlanModal
    // Update the row display text - format as "Tiers 1, 2, 3" etc.
    let enabledTiers = '';
    if (enabledTierIds.length === 1) {
      enabledTiers = `Tier ${enabledTierIds[0]}`;
    } else if (enabledTierIds.length > 1) {
      enabledTiers = `Tiers ${enabledTierIds.join(', ')}`;
    }

    setRows((prev) =>
      prev.map((row) => {
        if (row.slug === 'tiers') {
          const updated = { ...row, [tierModalPlanField]: enabledTiers };
          // Also update the value field with tier slugs array
          updated[`${tierModalPlanField}_value`] = enabledTierSlugs;
          return updated;
        }
        return row;
      })
    );

    // Close tier modal
    setTierModalOpen(false);
    setTierModalPlanField(null);

    // Don't open UpdatePlanModal - user will open it when clicking Save button
  };

  // Custom handlers for filter changes
  const handleAccountChange = (newAccount) => {
    // OlcDropdownSearch may return an array, extract first value or null
    const accountValue = Array.isArray(newAccount)
      ? newAccount.length > 0
        ? newAccount[0]
        : null
      : newAccount;

    // Clear plan data when account changes or becomes empty
    dispatch({
      type: SET_ADMIN_PRICING_PLANS_DATA,
      payload: {
        plans: [],
        rows: [],
      },
    });
    setRows([]);
    setVersionRows([]);

    setAccount(accountValue);

    // Clear version when account changes or is cleared
    setFeatureVersion(null);
    setFeatureVersionOptions([]);

    // Fetch versions for the selected account (call API even if id is missing, use empty string)
    // If account label is present, call with empty org_id
    // Auto-select default version when account changes
    if (accountValue) {
      fetchVersionsData(accountValue.id || '', true);
    } else {
      // Only clear if account is completely null/undefined
      setFeatureVersionOptions([]);
      setFeatureVersion(null);
    }
  };

  const handleFeatureVersionChange = (newVersion) => {
    // OlcDropdownSearch may return an array, extract first value or null
    const versionValue = Array.isArray(newVersion)
      ? newVersion.length > 0
        ? newVersion[0]
        : null
      : newVersion;
    setFeatureVersion(versionValue);

    // Fetch plan data when version is selected, or clear if no version
    if (versionValue?.id) {
      fetchPlansDataByVersion(versionValue.id);
    } else {
      // Clear plan data when version is cleared
      dispatch({
        type: SET_ADMIN_PRICING_PLANS_DATA,
        payload: {
          plans: [],
          rows: [],
        },
      });
      setRows([]);
      setVersionRows([]);
    }
  };

  // Fetch filters data on mount
  useEffect(() => {
    fetchFiltersData();

    // Cleanup function - clear plan data on component unmount
    return () => {
      dispatch({
        type: SET_ADMIN_PRICING_PLANS_DATA,
        payload: {
          plans: [],
          rows: [],
        },
      });
      setRows([]);
      setVersionRows([]);
    };
  }, []);

  // Fetch plan options when account changes
  useEffect(() => {
    fetchPlanOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  // Update rows when plans data is loaded
  useEffect(() => {
    if (plansArray.length > 0) {
      const transformedRows = transformPlansDataToRows(plansArray, planOptions);
      setRows(transformedRows);

      // Transform version details
      const transformedVersionRows = transformVersionDetailsData(plansData?.meta || {});
      setVersionRows([transformedVersionRows]);

      // Initialize tier access state from API data
      const tiersRow = transformedRows.find((row) => row.slug === 'tiers');
      if (tiersRow) {
        const newTierAccess = {};
        plansArray.forEach((plan, idx) => {
          const planField = `plan${idx + 1}`;
          const tierValue = tiersRow[`${planField}_value`] || tiersRow[planField];

          if (Array.isArray(tierValue)) {
            // Initialize tier access from API response
            const tierAccess = {};
            tierValue.forEach((tierSlug) => {
              const tierId = tierSlug ? parseInt(tierSlug.replace('tier_', '')) : null;
              if (tierId) {
                tierAccess[tierId] = true;
              }
            });
            newTierAccess[planField] = tierAccess;
          }
        });
        if (Object.keys(newTierAccess).length > 0) {
          setTierAccessByPlan((prev) => ({ ...prev, ...newTierAccess }));
        }
      }
    } else {
      setRows([]);
      setVersionRows([]);
    }
  }, [plansArray, data]);

  const handleDeletePlan = async () => {
    try {
      // Use the selected feature version ID
      if (!featureVersion?.id) {
        dispatch(failure('Please select a version to delete'));
        return;
      }

      const response = await dispatch(deletePlanVersion(featureVersion.id));
      if (response.status === 200) {
        dispatch(success(response.data.message || 'Plan version deleted successfully'));

        // Clear plan data
        dispatch({
          type: SET_ADMIN_PRICING_PLANS_DATA,
          payload: {
            plans: [],
            rows: [],
          },
        });
        setRows([]);
        setVersionRows([]);

        // Re-fetch versions and select default version (or first if no default)
        if (account?.id) {
          const versionsCount = await fetchVersionsData(account.id || '', true);

          // Check if no versions are available after refetch
          if (versionsCount === 0) {
            // No versions available, switch to "all standard account" (id === null)
            const accountWithNullId = accountOptions.find(
              (acc) => acc.id === null || acc.id === undefined || acc.id === ''
            );
            if (accountWithNullId) {
              // Clear plan data before switching account
              dispatch({
                type: SET_ADMIN_PRICING_PLANS_DATA,
                payload: {
                  plans: [],
                  rows: [],
                },
              });
              setRows([]);
              setVersionRows([]);
              setFeatureVersion(null);

              // Switch to "all standard account"
              setAccount(accountWithNullId);
              // Fetch versions for the account with null id
              await fetchVersionsData('', true);
            }
          }
        }
      } else {
        dispatch(failure(response?.data?.message || 'Failed to delete plan version'));
      }
    } catch (error) {
      dispatch(
        failure(error?.response?.data?.message || error?.message || 'Failed to delete plan version')
      );
    }
  };

  return (
    <Container maxWidth="xxl" className="olcAdminSectionPage">
      <Box className="plansWrapper">
        <Box className="plansTopFilters">
          <Box className="plansControlsGroup">
            <Box className="plansTableHeader">
              <Typography variant="h6" className="pricingControlsSectionTitle">
                Plans
              </Typography>
            </Box>

            <Box className="plansFilterGroup">
              <Box className="plansFilterGroupItem">
                <InputLabel className="plansFilterGroupItemLabel">Account</InputLabel>
                <OlcDropdownSearch
                  options={
                    accountSearchTerm.trim()
                      ? accountOptions // Show all options when searching (client-side filtering will handle it)
                      : accountOptions.slice(0, 10) // Show only first 10 when no search
                  }
                  selectedValue={account}
                  setSelectedValue={handleAccountChange}
                  clearable={true}
                  placeHolderText="Account"
                  noOptionText="No options found"
                  component="multipleIds"
                  searchAutocomplete
                  searchFieldPlaceholderText="for account"
                  className={account ? 'activeState filterControl' : 'inactiveState filterControl'}
                  activeState={true}
                  showSelectedValueTooltip
                  onSearchChange={(searchValue) => {
                    setAccountSearchTerm(searchValue || '');
                  }}
                />
              </Box>

              <Box className="plansFilterGroupItem">
                <InputLabel className="plansFilterGroupItemLabel">Feature Version</InputLabel>
                <OlcDropdownSearch
                  options={featureVersionOptions}
                  selectedValue={featureVersion}
                  setSelectedValue={handleFeatureVersionChange}
                  clearable={true}
                  placeHolderText="Feature Version"
                  noOptionText="No options found"
                  component="multipleIds"
                  searchAutocomplete
                  searchFieldPlaceholderText="version"
                  className={
                    featureVersion ? 'activeState filterControl' : 'inactiveState filterControl'
                  }
                  activeState={true}
                  showSelectedValueTooltip
                />
              </Box>
              {featureVersion?.id &&
                plansArray.length > 0 &&
                rows.length > 0 &&
                featureVersion?.status !== 'active' && (
                  <OlcButton
                    variant="secondary"
                    className="deleteBtn"
                    onClick={() =>
                      setConfirmDeleteModal((m) => ({
                        ...m,
                        open: true,
                        handleOk: async () => {
                          setConfirmDeleteModal((mm) => ({ ...mm, loading: true }));
                          await handleDeletePlan();
                          setConfirmDeleteModal((mm) => ({ ...mm, open: false, loading: false }));
                        },
                        handleClose: () => {
                          setConfirmDeleteModal((mm) => ({ ...mm, open: false, loading: false }));
                        },
                      }))
                    }
                  >
                    Delete
                  </OlcButton>
                )}
            </Box>
          </Box>

          <Box className="plansActionsWrapper">
            {isEditing && (
              <>
                <OlcButton
                  variant="primary"
                  className="saveBtn"
                  onClick={() => setUpdatePlanOpen(true)}
                >
                  Save
                </OlcButton>
                <OlcButton variant="secondary" className="cancelBtn" onClick={handleCancel}>
                  Cancel
                </OlcButton>
              </>
            )}
            <OlcButton
              variant="secondary"
              dropdown
              expanded={actionsOpen}
              id="plans-actions-button"
              aria-controls={actionsOpen ? 'plans-actions-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={actionsOpen ? 'true' : undefined}
              onClick={handleActionsClick}
              className="actionsBtn"
            >
              Actions
            </OlcButton>
            <OlcActionMenu
              id="plans-actions-menu"
              anchorEl={actionsAnchorEl}
              open={actionsOpen}
              onClose={handleActionsClose}
              disableScrollLock
              MenuListProps={{
                'aria-labelledby': 'plans-actions-button',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              sx={{
                marginTop: '4px',
              }}
            >
              {rows.length > 0 && (
                <MenuItem
                  className="menuItem"
                  onClick={() => {
                    // Just enable edit mode (don't open UpdatePlanModal)
                    handleStartEdit();
                  }}
                >
                  Update Plan
                </MenuItem>
              )}
              <MenuItem
                className="menuItem"
                onClick={() => {
                  setAssignPlanOpen(true);
                  handleActionsClose();
                }}
              >
                Assign Plan
              </MenuItem>
            </OlcActionMenu>
          </Box>
        </Box>

        <PricingControlsTable
          className="pricing-controls-table plansTable"
          rows={rows}
          columns={columns}
          pagination={{}}
          columnHeaderHeight={48}
          showPagination={false}
          showNoRows={(!featureVersion || !featureVersion?.id) && rows.length === 0}
          loading={loading}
        />

        <Divider sx={{ margin: '24px 0' }} />

        <PricingControlsTable
          className="pricing-controls-table plansTable"
          rows={versionRows || []}
          columns={versionColumns}
          pagination={{}}
          columnHeaderHeight={48}
          showPagination={false}
          showNoRows={(!featureVersion || !featureVersion?.id) && versionRows.length === 0}
          loading={loading}
        />
      </Box>

      <ConfirmModal openConfirmModal={confirmDeleteModal} />

      <AssignPlanModal
        open={assignPlanOpen}
        handleClose={() => setAssignPlanOpen(false)}
        onOpenUpdatePlan={() => setUpdatePlanOpen(true)}
        onPlanAssigned={() => {
          // Refetch account data after plan is assigned
          fetchFiltersData();
        }}
      />

      <UpdatePlanModal
        open={updatePlanOpen}
        handleClose={() => {
          setUpdatePlanOpen(false);
          // Don't clear form data here - keep it for when user clicks Save
        }}
        onSave={handleSave}
        formData={updatePlanFormData}
        setFormData={setUpdatePlanFormData}
      />

      <OLCBaseModal
        open={tierModalOpen}
        onClose={handleTierModalClose}
        onSubmit={handleTierModalSave}
        title="Tier Pricing Access"
        subTitle="Specify the tier levels you want this account to gain access to."
        primaryText="Save"
        secondaryText="Cancel"
        width={640}
        paperClassName="tierPricingModalWrapper"
        disableScrollLock={false}
      >
        <Box className="tierModalContent">
          <Box className="tierModalHeader">
            <Typography className="headerCell">Tier</Typography>
            <Typography className="headerCell">Quantity</Typography>
            <Typography className="headerCell">Access</Typography>
          </Box>
          {(() => {
            // Get tiers from the current row data
            const tiersRow = rows.find((row) => row.slug === 'tiers');
            const availableTiers = tiersRow?.tiers || [];

            return availableTiers.map((tier) => {
              // Extract tier number from slug (e.g., "tier_1" -> 1)
              const tierId = tier.slug ? parseInt(tier.slug.replace('tier_', '')) : tier.id;
              const tierLabel = tier.label || tier.tier || `Tier ${tierId}`;
              const tierRange = tier.qty_range || tier.range || '-';
              const currentTierAccess = tierAccessByPlan?.[tierModalPlanField || 'plan1'] || {};
              const isChecked = !!currentTierAccess[tierId];

              // No restrictions - allow users to select any tier
              // Validation will happen on save button click
              const isDisabled = false;

              return (
                <Box key={tier.slug || tier.id} className="tierModalRow">
                  <Typography className="tierCell">{tierLabel}</Typography>
                  <Typography className="tierCell">{tierRange}</Typography>
                  <Checkbox
                    className="tierCheckbox"
                    checked={isChecked}
                    disabled={isDisabled}
                    onChange={() => handleTierToggle(tierId)}
                    icon={<img src={UncheckedCIcon} alt="unchecked" />}
                    checkedIcon={<img src={CheckedCIcon} alt="checked" />}
                  />
                </Box>
              );
            });
          })()}
        </Box>
      </OLCBaseModal>
    </Container>
  );
};

export default Plans;
